import {
  CTCSS_TONES,
  DTCS_CODES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 0x40
const MEMORY_SIZE = 0x1c00
const CHANNEL_SIZE = 24
const CHANNELS_PER_BAND = 128
const UPPER_START = 0x0400
const LOWER_START = 0x1000

type LT725UVSpec = {
  id: string
  vendor: string
  model: string
  summary: string
  threePowerBands: boolean
}

export type LT725UVProfile = {
  protocol: 'lt725uv'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 19200
  channelMinimum: 1
  channelMaximum: number
  nameLength: 7
  memorySize: number
  modelId: string
  powerLevels: readonly ['Low', 'High']
  tones: readonly number[]
  dtcsCodes: readonly number[]
  threePowerBands: boolean
}

const SPECS: readonly LT725UVSpec[] = [
  {
    id: 'luiton-lt-725uv',
    vendor: 'LUITON',
    model: 'LT-725UV',
    summary: 'LUITON dual-band mobile with upper and lower 128-channel bands.',
    threePowerBands: false,
  },
  {
    id: 'baojie-bj-218',
    vendor: 'Baojie',
    model: 'BJ-218',
    summary: 'Baojie BJ-218 dual-band mobile in the LT-725UV family.',
    threePowerBands: false,
  },
  {
    id: 'baojie-bj-318',
    vendor: 'Baojie',
    model: 'BJ-318',
    summary: 'Baojie BJ-318 tri-power dual-band mobile.',
    threePowerBands: true,
  },
  {
    id: 'zastone-bj-218',
    vendor: 'Zastone',
    model: 'BJ-218',
    summary: 'Zastone-branded Baojie BJ-218 dual-band mobile.',
    threePowerBands: false,
  },
  {
    id: 'hesenate-bj-218',
    vendor: 'Hesenate',
    model: 'BJ-218',
    summary: 'Hesenate-branded Baojie BJ-218 dual-band mobile.',
    threePowerBands: false,
  },
]

export const LT725UV_PROFILES: readonly LT725UVProfile[] = SPECS.map(
  (spec) => ({
    protocol: 'lt725uv',
    id: spec.id,
    vendor: spec.vendor,
    model: spec.model,
    summary: spec.summary,
    tags: [
      'Experimental',
      'Read + write',
      '2 bands × 128 channels',
      '7-character names',
    ],
    baudRate: 19200,
    channelMinimum: 1,
    channelMaximum: CHANNELS_PER_BAND * 2,
    nameLength: 7,
    memorySize: MEMORY_SIZE,
    modelId: spec.model,
    powerLevels: ['Low', 'High'],
    tones: CTCSS_TONES,
    dtcsCodes: DTCS_CODES,
    threePowerBands: spec.threePowerBands,
  }),
)

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'Low',
    skip: false,
  }
}

function channelOffset(number: number): number {
  if (!Number.isInteger(number) || number < 1 || number > 256) {
    throw new Error('LT-725UV channel numbers must be between 1 and 256.')
  }
  const local = (number - 1) % CHANNELS_PER_BAND
  return (number <= CHANNELS_PER_BAND ? UPPER_START : LOWER_START) +
    local * CHANNEL_SIZE
}

function readUint32BE(raw: Uint8Array, offset: number): number {
  return (
    (raw[offset] << 24) |
    (raw[offset + 1] << 16) |
    (raw[offset + 2] << 8) |
    raw[offset + 3]
  ) >>> 0
}

function writeUint32BE(raw: Uint8Array, offset: number, value: number): void {
  raw[offset] = (value >>> 24) & 0xff
  raw[offset + 1] = (value >>> 16) & 0xff
  raw[offset + 2] = (value >>> 8) & 0xff
  raw[offset + 3] = value & 0xff
}

function readUint16BE(raw: Uint8Array, offset: number): number {
  return (raw[offset] << 8) | raw[offset + 1]
}

function writeUint16BE(raw: Uint8Array, offset: number, value: number): void {
  raw[offset] = (value >>> 8) & 0xff
  raw[offset + 1] = value & 0xff
}

function bbcdToDecimal(value: number): number {
  let result = 0
  let factor = 1
  while (value > 0) {
    result += (value & 0x0f) * factor
    value >>>= 4
    factor *= 10
  }
  return result
}

function decimalToBbcd(value: number): number {
  let result = 0
  let shift = 0
  let remaining = Math.max(0, Math.trunc(value))
  while (remaining > 0) {
    result |= (remaining % 10) << shift
    remaining = Math.floor(remaining / 10)
    shift += 4
  }
  return result
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(raw: Uint8Array, offset: number): DecodedTone {
  const packed = readUint16BE(raw, offset)
  const digital = (packed & 0x8000) !== 0
  const polarity = (packed & 0x4000) !== 0 ? 'R' : 'N'
  const value = packed & 0x3fff
  if (value === 0x3fff) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (!digital) {
    return {
      mode: 'ctcss',
      ctcss: value / 10,
      dtcs: null,
      polarity: 'N',
    }
  }
  return {
    mode: 'dtcs',
    ctcss: null,
    dtcs: bbcdToDecimal(value),
    polarity,
  }
}

function encodeTone(
  raw: Uint8Array,
  offset: number,
  tone: DecodedTone,
): void {
  if (tone.mode === 'none') {
    writeUint16BE(raw, offset, 0xffff)
    return
  }
  if (tone.mode === 'ctcss') {
    writeUint16BE(raw, offset, Math.round((tone.ctcss ?? 0) * 10))
    return
  }
  writeUint16BE(
    raw,
    offset,
    0x8000 |
      (tone.polarity === 'R' ? 0x4000 : 0) |
      decimalToBbcd(tone.dtcs ?? 0),
  )
}

function selectedTone(raw: Uint8Array): DecodedTone {
  const transmit = decodeTone(raw, 11)
  return transmit.mode === 'none' ? decodeTone(raw, 4) : transmit
}

export function parseLT725UVChannel(
  image: Uint8Array,
  profile: LT725UVProfile,
  number: number,
): WebChannel {
  const offset = channelOffset(number)
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || raw[0] === 0xff) {
    return emptyChannel(number)
  }
  const receive = readUint32BE(raw, 0)
  if (!receive || receive === 0xffffffff) return emptyChannel(number)
  const transmit = readUint32BE(raw, 7)
  const tone = selectedTone(raw)
  const nameLength = Math.min(raw[16], 7)
  const name = String.fromCharCode(...raw.slice(17, 17 + nameLength))
    .replace(/[\u0000\ufffd]/g, '')
    .trim()
  const flags = raw[15]
  return {
    number,
    empty: false,
    name,
    rxFrequency: receive * 10,
    txFrequency: transmit === 0xffffffff ? null : transmit * 10,
    toneMode: tone.mode,
    ctcss: tone.ctcss,
    dtcs: tone.dtcs,
    dtcsPolarity: tone.polarity,
    mode: flags & 0x40 ? 'FM' : 'NFM',
    power: flags & 0x80 ? 'High' : 'Low',
    skip: false,
  }
}

export function parseLT725UVChannels(
  image: Uint8Array,
  profile: LT725UVProfile,
): WebChannel[] {
  return Array.from({ length: profile.channelMaximum }, (_, index) =>
    parseLT725UVChannel(image, profile, index + 1),
  )
}

export function applyLT725UVChannel(
  image: Uint8Array,
  profile: LT725UVProfile,
  channel: WebChannel,
): void {
  const offset = channelOffset(channel.number)
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  raw.fill(0xff)
  raw.fill(0, 15, 17)
  writeUint32BE(raw, 0, Math.round(channel.rxFrequency / 10))
  writeUint32BE(
    raw,
    7,
    channel.txFrequency ? Math.round(channel.txFrequency / 10) : 0xffffffff,
  )
  const tone: DecodedTone =
    channel.toneMode === 'ctcss'
      ? {
          mode: 'ctcss',
          ctcss: channel.ctcss,
          dtcs: null,
          polarity: 'N',
        }
      : channel.toneMode === 'dtcs'
        ? {
            mode: 'dtcs',
            ctcss: null,
            dtcs: channel.dtcs,
            polarity: channel.dtcsPolarity,
          }
        : { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  encodeTone(raw, 4, tone)
  encodeTone(raw, 11, tone)
  raw[13] = 0
  raw[14] = 0
  raw[15] =
    (channel.power === 'High' ? 0x80 : 0) |
    (channel.mode === 'FM' ? 0x40 : 0)
  const name = channel.name.slice(0, 7)
  raw[16] = name.length
  raw.fill(0xff, 17, 24)
  for (let index = 0; index < name.length; index += 1) {
    raw[17 + index] = name.charCodeAt(index) & 0xff
  }
}

function frame(command: string, address: number, length: number): Uint8Array {
  const result = new Uint8Array(8)
  result.set(new TextEncoder().encode(command))
  result[4] = (address >>> 8) & 0xff
  result[5] = address & 0xff
  result[6] = (length >>> 8) & 0xff
  result[7] = length & 0xff
  return result
}

async function enter(transport: RadioTransport): Promise<void> {
  await transport.discardInput()
  await transport.write(new TextEncoder().encode('PROM_LIN'))
  if ((await transport.readExact(1, 2500))[0] !== ACK) {
    throw new Error('The radio refused LT-725UV programming mode.')
  }
}

async function exit(transport: RadioTransport): Promise<void> {
  await transport.write(new TextEncoder().encode('EXIT'))
}

export async function readLT725UVRadioWithTransport(
  profile: LT725UVProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enter(transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let block = 0; block < total; block += 1) {
      const address = block * BLOCK_SIZE
      await transport.write(frame('READ', address, BLOCK_SIZE))
      image.set(
        await transport.readExact(BLOCK_SIZE, 3000),
        address,
      )
      onProgress?.({
        current: block + 1,
        total,
        label: 'Reading LT-725UV memory',
      })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await exit(transport)
  }
}

export async function writeLT725UVRadioWithTransport(
  profile: LT725UVProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  await enter(transport)
  try {
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let block = 0; block < total; block += 1) {
      const address = block * BLOCK_SIZE
      const packet = new Uint8Array(8 + BLOCK_SIZE)
      packet.set(frame('WRIE', address, BLOCK_SIZE))
      packet.set(image.slice(address, address + BLOCK_SIZE), 8)
      await transport.write(packet)
      if ((await transport.readExact(1, 3000))[0] !== ACK) {
        throw new Error(
          `The radio rejected LT-725UV block 0x${address.toString(16)}.`,
        )
      }
      onProgress?.({
        current: block + 1,
        total,
        label: 'Writing LT-725UV memory',
      })
    }
  } finally {
    await exit(transport)
  }
}
